/**
 * Permission utility functions for frontend permission checks
 * 
 * This module provides utilities to check user permissions on the frontend
 * to hide/show UI elements and prevent unauthorized actions.
 */

/**
 * All available permissions in the system
 * Synced with backend RolePermissionSeeder
 */
export const PERMISSIONS = {
  // Group Settings
  VIEW_GROUP_SETTINGS: 'view_group_settings',
  EDIT_GROUP_SETTINGS: 'edit_group_settings',
  EDIT_GROUP_INFO: 'edit_group_info',
  UPDATE_FEATURES: 'update_features',

  // Member Management
  VIEW_MEMBERS: 'view_members',
  CREATE_MEMBER: 'create_member',
  EDIT_MEMBER: 'edit_member',
  DELETE_MEMBER: 'delete_member',
  IMPORT_MEMBERS: 'import_members',
  EXPORT_MEMBERS: 'export_members',

  // Admin Management
  VIEW_ADMINS: 'view_admins',
  CREATE_ADMIN: 'create_admin',
  EDIT_ADMIN: 'edit_admin',
  DELETE_ADMIN: 'delete_admin',

  // Role & Permission Management
  VIEW_ROLES: 'view_roles',
  CREATE_ROLE: 'create_role',
  EDIT_ROLE: 'edit_role',
  DELETE_ROLE: 'delete_role',
  ASSIGN_ROLES: 'assign_roles',

  // Contribution Management
  VIEW_CONTRIBUTIONS: 'view_contributions',
  CREATE_CONTRIBUTION: 'create_contribution',
  EDIT_CONTRIBUTION: 'edit_contribution',
  DELETE_CONTRIBUTION: 'delete_contribution',
  EXPORT_CONTRIBUTIONS: 'export_contributions',
  CREATE_CONTRIBUTION_DRIVE: 'create_contribution_drive',
  EDIT_CONTRIBUTION_DRIVE: 'edit_contribution_drive',
  DELETE_CONTRIBUTION_DRIVE: 'delete_contribution_drive',

  // Loan Management
  VIEW_ALL_LOANS: 'view_all_loans',
  CREATE_LOAN: 'create_loan',
  EDIT_LOAN: 'edit_loan',
  DELETE_LOAN: 'delete_loan',
  APPROVE_LOAN: 'approve_loan',
  REJECT_LOAN: 'reject_loan',
  BULK_APPROVE_LOANS: 'bulk_approve_loans',
  VIEW_LOAN_REPAYMENT: 'view_loan_repayment',
  ADD_LOAN_REPAYMENT: 'add_loan_repayment',
  DELETE_LOAN_REPAYMENT: 'delete_loan_repayment',
  UPDATE_LOAN_ELIGIBILITY_SETTING: 'update_loan_eligibility_setting',
  MANAGE_LOAN_TYPES: 'manage_loan_types',
  VIEW_LOAN_TYPES: 'view_loan_types',
  DELETE_LOAN_TYPE: 'delete_loan_type',

  // Welfare Management
  VIEW_WELFARE_PLANS: 'view_welfare_plans',
  VIEW_ALL_WELFARE_PLANS: 'view_all_welfare_plans',
  CREATE_WELFARE_PLANS: 'create_welfare_plans',
  EDIT_WELFARE_PLANS: 'edit_welfare_plans',
  DELETE_WELFARE_PLANS: 'delete_welfare_plans',
  MANAGE_WELFARE_PLAN_MEMBERS: 'manage_welfare_plan_members',
  CLOSE_WELFARE_PLANS: 'close_welfare_plans',
  VIEW_WELFARE_PLAN_STATISTICS: 'view_welfare_plan_statistics',
  MANAGE_WELFARE_PLAN_INVITES: 'manage_welfare_plan_invites',

  // Merry-Go-Round
  CREATE_GROUP_ROTATION: 'create_group_rotation',
  EDIT_GROUP_ROTATION: 'edit_group_rotation',
  DELETE_GROUP_ROTATION: 'delete_group_rotation',

  // Financial Management
  VIEW_DISBURSEMENTS: 'view_disbursements',
  CREATE_DISBURSEMENT: 'create_disbursement',
  APPROVE_DISBURSEMENT: 'approve_disbursement',

  // SMS Management
  VIEW_SMS_HISTORY: 'view_sms_history',
  SEND_BULK_SMS: 'send_bulk_sms',
  VIEW_SMS_QUOTA: 'view_sms_quota',

  // Document Management
  VIEW_DOCUMENTS: 'view_documents',
  UPLOAD_DOCUMENTS: 'upload_documents',
  DELETE_DOCUMENTS: 'delete_documents',

  // Transaction Management
  VIEW_TRANSACTIONS: 'view_transactions',
  VIEW_GROUP_BALANCE: 'view_group_balance',
  DELETE_TRANSACTION: 'delete_transaction',

  // Expense Management
  VIEW_EXPENSES: 'view_expenses',
  CREATE_EXPENSE: 'create_expense',
  EDIT_EXPENSE: 'edit_expense',
  DELETE_EXPENSE: 'delete_expense',

  // Shares Management
  VIEW_SHARES: 'view_shares',
  CREATE_SHARE: 'create_share',
  EDIT_SHARE: 'edit_share',
  DELETE_SHARE: 'delete_share',
  VIEW_ALL_SHARES: 'view_all_shares',

  // Fines Management
  VIEW_FINES: 'view_fines',
  CREATE_FINE: 'create_fine',
  EDIT_FINE: 'edit_fine',
  DELETE_FINE: 'delete_fine',

  // Meeting Management
  VIEW_MEETINGS: 'view_meetings',
  CREATE_MEETING: 'create_meeting',
  EDIT_MEETING: 'edit_meeting',
  DELETE_MEETING: 'delete_meeting',
  CREATE_MEETING_NOTE: 'create_meeting_note',
  EDIT_MEETING_NOTE: 'edit_meeting_note',
  DELETE_MEETING_NOTE: 'delete_meeting_note',

  // Pending Members
  VIEW_PENDING_MEMBERS: 'view_pending_members',
  APPROVE_PENDING_MEMBERS: 'approve_pending_members',
  REJECT_PENDING_MEMBERS: 'reject_pending_members',
  DELETE_PENDING_MEMBERS: 'delete_pending_members',
  GENERATE_ONBOARDING_LINKS: 'generate_onboarding_links',

  // Assets
  VIEW_ASSETS: 'view_assets',
  CREATE_ASSET: 'create_asset',
  EDIT_ASSET: 'edit_asset',
  DELETE_ASSET: 'delete_asset',

  // Loan Advanced Features
  WRITE_OFF_LOAN: 'write_off_loan',
  APPROVE_WRITE_OFF: 'approve_write_off',
  RESTRUCTURE_LOAN: 'restructure_loan',
  APPROVE_RESTRUCTURE: 'approve_restructure',
  MANAGE_COLLATERAL: 'manage_collateral',
  VERIFY_COLLATERAL: 'verify_collateral',
  REFINANCE_LOAN: 'refinance_loan',
  APPROVE_REFINANCE: 'approve_refinance',
  GENERATE_LOAN_STATEMENT: 'generate_loan_statement',
};

/**
 * Check if user has a specific permission
 * @param {Object} user - User object with permissions array
 * @param {string} permission - Permission name to check
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;
  
  // Group owners have all permissions
  if (user.is_group_owner || user.isGroupOwner) return true;
  
  // Check if user has the permission
  if (user.permissions && Array.isArray(user.permissions)) {
    // Handle both array of strings and array of objects
    return user.permissions.some(p => {
      if (typeof p === 'string') {
        return p === permission;
      }
      if (typeof p === 'object' && p !== null) {
        return p.name === permission;
      }
      return false;
    });
  }
  
  return false;
};

/**
 * Check if user has any of the given permissions
 * @param {Object} user - User object with permissions array
 * @param {string[]} permissions - Array of permission names
 * @returns {boolean}
 */
export const hasAnyPermission = (user, permissions) => {
  if (!user) return false;
  if (user.is_group_owner || user.isGroupOwner) return true;
  
  return permissions.some(permission => hasPermission(user, permission));
};

/**
 * Check if user has all of the given permissions
 * @param {Object} user - User object with permissions array
 * @param {string[]} permissions - Array of permission names
 * @returns {boolean}
 */
export const hasAllPermissions = (user, permissions) => {
  if (!user) return false;
  if (user.is_group_owner || user.isGroupOwner) return true;
  
  return permissions.every(permission => hasPermission(user, permission));
};

/**
 * Get permission error message
 * @param {string} permission - Permission name
 * @returns {string}
 */
export const getPermissionErrorMessage = (permission) => {
  return `You don't have permission to perform this action. Required permission: ${permission}`;
};
